import { makeAutoObservable } from 'mobx'
import Loader from 'reusable/mobx-helpers/Loader'
import { apiClient } from 'api/trpc/trpc-client'
import { snackbarSuccess } from 'reusable/mui/snackbar/snackbar'
import { ChargebeeDto } from '@gap/backend/app-services/chargebee/models/ChargebeeDto'
import { tierNameDtoSchema } from '@gap/utils/tier/tier-config'
import { Analytics, AnalyticsEvent } from 'analytics/analytics'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { getStaticPlans } from './static-plan-catalog'

const isIndividualPlan = (plan: ChargebeeDto.PurchasableItem): boolean => plan.planType === 'individual'

/**
 * INTERIM (NO-455): same class name and public surface as the Chargebee branch's
 * BillingStore (PR #471) so PlanSelection / PlanGrid render unchanged, but the plan
 * catalogue is the static one (static-plan-catalog.ts) and choosing a plan writes the
 * tier through the existing org.upgradeTier flow — the account upgrades immediately and
 * our sales team arranges billing. PR #471 replaces this file wholesale with the
 * Chargebee-backed store (live catalogue + in-app checkout).
 */
export class BillingStore {
   sp
   loader = new Loader(false)
   checkoutLoader = new Loader(false)
   plans: ChargebeeDto.PurchasableItem[] = []
   planTabIndex = 0
   // The card whose upgrade is in flight — drives that card's (and only that card's) spinner.
   changingItemPriceId: string | null = null

   constructor(sp: { orgGuardStore: OrgGuardStore; onPlanChanged?: () => void }) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get currentTierName() {
      return this.sp.orgGuardStore.userData.userOrg?.tierName
   }

   // The static catalogue's itemId is the exact tier name, so the org's current tier maps
   // straight onto a card. Legacy tiers (Start/Focus/Scale/Enterprise) match no card: no
   // "Current plan" chip, and every button reads "Upgrade" (currentPlanPrice falls back to 0).
   get currentPlan() {
      return this.plans.find(plan => plan.itemId === this.currentTierName)
   }

   get currentPlanItemPriceId() {
      return this.currentPlan?.itemPriceId
   }

   get currentPlanPrice() {
      return this.plans.find(plan => plan.itemPriceId === this.currentPlanItemPriceId)?.price ?? 0
   }

   get individualPlans() {
      return this.plans.filter(isIndividualPlan)
   }

   get multiUserPlans() {
      return this.plans.filter(plan => !isIndividualPlan(plan))
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         this.plans = getStaticPlans(this.sp.orgGuardStore.org?.currencyCode)
         // Open on the tab holding the org's current plan (e.g. an Expand org lands on
         // Multi-user). Trial and legacy orgs match no card and default to Individual.
         if (this.currentPlan && !isIndividualPlan(this.currentPlan)) {
            this.planTabIndex = 1
         }
      })
   }

   changePlanTab = (_event: React.SyntheticEvent, tabIndex: number) => {
      this.planTabIndex = tabIndex
   }

   changePlanButtonHandler = (event: React.MouseEvent<HTMLElement>) => {
      const itemPriceId = event.currentTarget.getAttribute('data-item-price-id')
      if (itemPriceId) {
         this.changePlan(itemPriceId)
      }
   }

   // The static catalogue has no custom (Enterprise) plans, but PlanGrid still binds this handler.
   requestCustomPlanButtonHandler = () => {}

   changePlan = async (itemPriceId: string) => {
      const targetPlan = this.plans.find(plan => plan.itemPriceId === itemPriceId)
      if (!targetPlan) {
         return
      }
      this.changingItemPriceId = itemPriceId
      // No try/catch: apiClient's trpcFetcher already surfaces a failed upgrade (e.g. the
      // downgrade rejection) in the global error dialog, and everything after the await is
      // skipped on failure — same convention as the pre-NO-455 upgrade flow.
      await this.checkoutLoader.executeLoading(async () => {
         const previousTierName = this.currentTierName
         const requestedTierName = tierNameDtoSchema.parse(targetPlan.itemId)
         const { tierName, tierMaxUsers } = await apiClient.org.upgradeTier.query({ tierName: requestedTierName })
         this.sp.orgGuardStore.updateUserOrgData({ tierName, tierMaxUsers })

         Analytics.emitEvent(AnalyticsEvent.planUpgraded, {
            'Previous plan': previousTierName || 'Unknown',
            'New plan': tierName,
            'Changed by': this.sp.orgGuardStore.org?.orgType === 'gap' ? 'Gap' : 'Advisor',
         })

         this.sp.onPlanChanged?.()
         snackbarSuccess(`You're now on the ${tierName} plan. Our team will be in touch to arrange billing.`)
      }).finally(this.clearChangingPlan)
   }

   clearChangingPlan = () => {
      this.changingItemPriceId = null
   }
}

export const { Provider: BillingStoreProvider, useProvidedStore: useBillingStore } = createStoreContext(BillingStore)
